import React, { createContext, useContext, useReducer, useEffect } from 'react';

// Initial state
const initialState = {
    isLoading: true,
    selectedFilter: 'average_score',
    rawData: null,
    displayData: [],
    error: null,
};

const ACTIONS = {
    SET_LOADING: 'SET_LOADING',
    SET_FILTER: 'SET_FILTER',
    SET_ERROR: 'SET_ERROR',
    SET_RAW_DATA: 'SET_RAW_DATA',
    SET_DISPLAY_DATA: 'SET_DISPLAY_DATA',
};

const Context = createContext();

const appReducer = (state, action) => {
    switch (action.type) {
        case ACTIONS.SET_LOADING:
            return {
                ...state,
                isLoading: action.payload,
            };
        case ACTIONS.SET_FILTER:
            return {
                ...state,
                selectedFilter: action.payload,
            };
        case ACTIONS.SET_ERROR:
            return {
                ...state,
                error: action.payload,
            };
        case ACTIONS.SET_RAW_DATA:
            return {
                ...state,
                rawData: action.payload,
            };
        case ACTIONS.SET_DISPLAY_DATA:
            return {
                ...state,
                displayData: action.payload,
            };
        default:
            return state;
    }
};

export const AppProvider = ({ children }) => {
    const [state, dispatch] = useReducer(appReducer, initialState);

    const fetchData = async () => {
        const url = '/api/trends/the-solution';

        dispatch({ type: ACTIONS.SET_LOADING, payload: true });

        try {
            const response = await fetch(url);
            const apiData = await response.json();

            if(apiData.error) {
                throw new Error(apiData.message);
            }

            if(!apiData?.datasets) {
                throw new Error('No datasets found');
            }

            // Store raw API data - we'll transform it per filter
            dispatch({ type: ACTIONS.SET_RAW_DATA, payload: apiData });
            dispatch({ type: ACTIONS.SET_LOADING, payload: false });

        } catch (error) {
            dispatch({ type: ACTIONS.SET_ERROR, payload: error.message || 'An error occurred while fetching data' });
        }
    }



    useEffect(() => {
        fetchData();
    }, []);

    // Update displayData when rawData or selectedFilter changes
    useEffect(() => {
        if (state.rawData?.datasets && state.selectedFilter) {
            const displayData = buildFilterSpecificData(state.rawData.datasets, state.selectedFilter);
            dispatch({ type: ACTIONS.SET_DISPLAY_DATA, payload: displayData });
        }
    }, [state.rawData, state.selectedFilter]);

    // Build display data specific to the selected filter with only relevant years
    const buildFilterSpecificData = (datasets, selectedFilter) => {
        switch (selectedFilter) {
            case 'average_score':
                // Only show 2021-2025 (survey years)
                return datasets.survey_metrics?.data?.map(item => ({
                    year: item.year,
                    value: item.average_score
                })) || [];

            case 'spotlight_rating':
                // Only show 2009-2025 (spotlight years)
                return datasets.spotlight_metrics?.data?.map(item => ({
                    year: item.year,
                    value: item.spotlight_rating
                })) || [];

            case 'speech_controversies':
                // Show years where we have any controversy data, with breakdown
                const allControversyYears = new Set();
                const controversyBreakdown = {};

                // Collect years and initialize all properties to null
                datasets.controversy_metrics?.deplatformings?.data?.forEach(item => {
                    allControversyYears.add(item.year);
                    if (!controversyBreakdown[item.year]) {
                        controversyBreakdown[item.year] = {
                            deplatformings: null,
                            scholars_under_fire: null,
                            students_under_fire: null
                        };
                    }
                    controversyBreakdown[item.year].deplatformings = item.value;
                });

                datasets.controversy_metrics?.scholars_under_fire?.data?.forEach(item => {
                    allControversyYears.add(item.year);
                    if (!controversyBreakdown[item.year]) {
                        controversyBreakdown[item.year] = {
                            deplatformings: null,
                            scholars_under_fire: null,
                            students_under_fire: null
                        };
                    }
                    controversyBreakdown[item.year].scholars_under_fire = item.value;
                });

                datasets.controversy_metrics?.students_under_fire?.data?.forEach(item => {
                    allControversyYears.add(item.year);
                    if (!controversyBreakdown[item.year]) {
                        controversyBreakdown[item.year] = {
                            deplatformings: null,
                            scholars_under_fire: null,
                            students_under_fire: null
                        };
                    }
                    controversyBreakdown[item.year].students_under_fire = item.value;
                });

                // Return sorted years with controversy breakdown, null values won't be plotted
                return Array.from(allControversyYears)
                    .sort()
                    .map(year => ({
                        year,
                        value: controversyBreakdown[year]
                    }));

            default:
                return [];
        }
    };

    const setLoading = (loading) => {
        dispatch({ type: ACTIONS.SET_LOADING, payload: loading });
    };

    const setFilter = (filter) => {
        dispatch({ type: ACTIONS.SET_FILTER, payload: filter });
    };

    const actions = {
        setLoading,
        setFilter,
    };

    const value = {
        ...state,
        ...actions,
    };

    return (
        <Context.Provider value={value}>
            {children}
        </Context.Provider>
    );
};

export const useApp = () => {
    return useContext(Context);
};
